import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, concatMap, withLatestFrom, mergeMap, catchError } from 'rxjs/operators';

import { createEffect, ofType, Actions } from '@ngrx/effects';
import { RouterReducerState } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';

import { RouterStateUrl } from '@sd-portal/shared/util';

import { State } from '..';
import * as actions from './simulated-flight.actions';
import { SimulatedFlightAuditService } from '../../simulated-flight-audit.service';

@Injectable()
export class SimulatedFlightEffects {
  constructor(
    private readonly simulatedFlightAuditService: SimulatedFlightAuditService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  loadSimulatedFlightSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadSimulatedFlightSkeletons),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const crewMemberId =
          state.sharedAudit.simulatedFlight.simulatedFlightSkeletons.logbookSelectedPilot;
        return this.simulatedFlightAuditService.getSimulatedFlightSkeletons(crewMemberId).pipe(
          map(skeletons => actions.SimulatedFlightSkeletonsLoaded({ skeletons })),
          catchError(err => of(actions.SimulatedFlightAuditLoadError(err)))
        );
      })
    )
  );

  loadSimulatedFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadSimulatedFlightAudit),
      mergeMap(action =>
        this.simulatedFlightAuditService.getSimulatedFlightAudit(action.simulatedFlightId).pipe(
          map(audits =>
            actions.SimulatedFlightAuditLoaded({
              simulatedFlightId: action.simulatedFlightId,
              audits
            })
          ),
          catchError(err => of(actions.SimulatedFlightAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageSimulatedFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageSimulatedFlightAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.simulatedFlight.simulatedFlightAudit.lastAuditLogId;
        }
        return this.simulatedFlightAuditService
          .getSimulatedFlightAudit(
            action.simulatedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.SimulatedFlightAuditLoaded({
                simulatedFlightId: action.simulatedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.SimulatedFlightAuditLoadError(err)))
          );
      })
    )
  );
}
